import {useEffect, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {
  BadgeCounts,
  DEFAULT_BADGE_COUNTS,
  DiagnosticItem,
  FILTER_OPTION,
  FilterOption,
  SkillGuide,
  SkillItem,
  SkillSubjectGroup
} from '../types';
import getQueryVariables from '../getQueryVariables';
import {
  SkillPathSkillFieldsFragment,
  useAdaptiveAchievementLazyQuery,
  useAllSkillsLazyQuery,
  useToDoSkillsLazyQuery
} from '../queries/StudentSkillPath.queries.generated';
import {
  StudentDiagnosticListFieldsFragment,
  useStudentDiagnosticsListLazyQuery
} from '../queries/StudentDiagnostics.queries.generated';
import {isOpenDiagnostic} from 'client/DiagnosticPracticeView/availability';
import useClassrooms from '../../Sidebar/hooks/useClassrooms';

const toDiagnosticItem = (sd: StudentDiagnosticListFieldsFragment): DiagnosticItem => ({
  diagnosticId: sd.diagnosticId,
  name: sd.diagnostic.name,
  subjectName: sd.diagnostic.guide.subject.name,
  guideName: sd.diagnostic.guide.name,
  dueDate: sd.diagnosticSettings.dueDate ?? null
});

const toAvailabilityInput = (sd: StudentDiagnosticListFieldsFragment) => ({
  completedAt: sd.completedAt,
  startDate: sd.diagnosticSettings.startDate,
  endDate: sd.diagnosticSettings.endDate
});

// Match student-assignment ordering: nearest due date first, no-due-date last, tiebreak by name.
const dueDateMs = (d: string | null) => (d ? new Date(d).getTime() : Infinity);
const byDueDateThenName = (a: DiagnosticItem, b: DiagnosticItem): number =>
  dueDateMs(a.dueDate) - dueDateMs(b.dueDate) || a.name.localeCompare(b.name);

export const toOpenDiagnosticItems = (
  studentDiagnostics: StudentDiagnosticListFieldsFragment[]
): DiagnosticItem[] => {
  const now = new Date();
  return studentDiagnostics
    .filter((sd) => isOpenDiagnostic(toAvailabilityInput(sd), now))
    .map(toDiagnosticItem)
    .sort(byDueDateThenName);
};

interface SkillPathResult {
  subjectGroups: SkillSubjectGroup[];
  diagnostics: DiagnosticItem[];
}

const skillsCache = new Map<string, SkillPathResult>();

// Badges are shared across filters within the same classroom
const badgesCache = new Map<string, BadgeCounts>();

// Call this when leaving the dashboard entirely to force a fresh fetch on return.
export function clearSkillPathCache(): void {
  skillsCache.clear();
  badgesCache.clear();
}

function skillsCacheKey(
  classroomId: string | undefined,
  filter: FilterOption,
  diagnosticsEnabled: boolean
): string {
  return `${classroomId ?? ''}:${filter}:${diagnosticsEnabled}`;
}

function badgesCacheKey(classroomId: string | undefined): string {
  return classroomId ?? '';
}

interface RawSubject {
  id: string;
  name: string;
  urlSlug: string;
  guides: Array<{
    id: string;
    name: string;
    skills: SkillPathSkillFieldsFragment[];
  }>;
}

function mapSubjectsToSkillGroups(subjects: RawSubject[]): SkillSubjectGroup[] {
  return subjects.map((subject) => {
    const skillGuides: SkillGuide[] = subject.guides
      .filter((guide) => guide.skills.length > 0)
      .map((guide) => {
        const items: SkillItem[] = guide.skills.map((gl) => ({
          id: gl.id,
          name: gl.name,
          code: gl.code ?? null,
          myProficiencyLevel: gl.myProficiencyLevel ?? null,
          dueDate: gl.nearestSkillPathDueDate ?? null
        }));
        return {guideId: guide.id, guideName: guide.name, items};
      });
    return {
      subjectId: subject.id,
      subjectName: subject.name,
      browseUrl: `/adaptive-subjects/${subject.urlSlug}`,
      skillGuides
    };
  });
}

function parseBadgeCounts(json: string): BadgeCounts {
  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    return {
      1: parsed['1'] ?? 0,
      2: parsed['2'] ?? 0,
      3: parsed['3'] ?? 0
    };
  } catch {
    return DEFAULT_BADGE_COUNTS;
  }
}

interface UseStudentSkillPathResult {
  skillSubjectGroups: SkillSubjectGroup[];
  diagnostics: DiagnosticItem[];
  badgeCounts: BadgeCounts;
  isLoading: boolean;
}

const EMPTY_RESULT: SkillPathResult = {subjectGroups: [], diagnostics: []};

export function useStudentSkillPath(
  classroomId: string | undefined,
  filter: FilterOption
): UseStudentSkillPathResult {
  const {isFeatureEnabled: isAdaptiveDiagnosticsEnabled} =
    useIsFeatureFlagEnabled('adaptive_diagnostics');

  const sKey = skillsCacheKey(classroomId, filter, isAdaptiveDiagnosticsEnabled);
  const bKey = badgesCacheKey(classroomId);

  const [activeResult, setActiveResult] = useState<SkillPathResult | null>(
    () => skillsCache.get(sKey) ?? null
  );
  const [activeBadgeCounts, setActiveBadgeCounts] = useState<BadgeCounts>(
    () => badgesCache.get(bKey) ?? DEFAULT_BADGE_COUNTS
  );

  const [prevSKey, setPrevSKey] = useState(() => sKey);
  const [prevBKey, setPrevBKey] = useState(() => bKey);

  if (sKey !== prevSKey) {
    setPrevSKey(sKey);
    setActiveResult(skillsCache.get(sKey) ?? null);
  }

  if (bKey !== prevBKey) {
    setPrevBKey(bKey);
    setActiveBadgeCounts(badgesCache.get(bKey) ?? DEFAULT_BADGE_COUNTS);
  }

  const {classrooms} = useClassrooms();

  const [fetchToDoSkills] = useToDoSkillsLazyQuery();
  const [fetchAllSkills] = useAllSkillsLazyQuery();
  const [fetchAdaptiveAchievement] = useAdaptiveAchievementLazyQuery();
  const [fetchStudentDiagnostics] = useStudentDiagnosticsListLazyQuery();

  useEffect(() => {
    let cancelled = false;

    const skillsNeeded = !skillsCache.has(sKey);
    const badgesNeeded = !badgesCache.has(bKey);

    if (!skillsNeeded && !badgesNeeded) return;

    const queryVariables = getQueryVariables(
      classroomId,
      classrooms.map((classroom) => classroom.getId()).toArray()
    );

    const fetchSubjects = (): Promise<RawSubject[]> =>
      filter === FILTER_OPTION.TO_DO
        ? fetchToDoSkills(queryVariables).then((r) =>
            (r.data?.subjects?.results ?? []).map((s) => ({
              id: s.id,
              name: s.name,
              urlSlug: s.urlSlug,
              guides: s.guides.map((g) => ({
                id: g.id,
                name: g.name,
                skills: g.toDoSkills.slice(0, 5)
              }))
            }))
          )
        : fetchAllSkills(queryVariables).then((r) =>
            (r.data?.subjects?.results ?? []).map((s) => ({
              id: s.id,
              name: s.name,
              urlSlug: s.urlSlug,
              guides: s.guides.map((g) => ({id: g.id, name: g.name, skills: g.allSkills}))
            }))
          );

    const fetchDiagnostics = (): Promise<StudentDiagnosticListFieldsFragment[]> =>
      isAdaptiveDiagnosticsEnabled
        ? fetchStudentDiagnostics(queryVariables).then(
            (r) => r.data?.studentDiagnostics?.results ?? []
          )
        : Promise.resolve([]);

    const resultPromise: Promise<SkillPathResult | null> = skillsNeeded
      ? Promise.all([fetchSubjects(), fetchDiagnostics()]).then(([subjects, diagnostics]) => ({
          subjectGroups: mapSubjectsToSkillGroups(subjects),
          diagnostics: toOpenDiagnosticItems(diagnostics)
        }))
      : Promise.resolve(null);

    const badgesPromise: Promise<BadgeCounts | null> = badgesNeeded
      ? fetchAdaptiveAchievement(queryVariables).then((r) =>
          parseBadgeCounts(r.data?.adaptiveAchievement ?? '{}')
        )
      : Promise.resolve(null);

    Promise.all([resultPromise, badgesPromise])
      .then(([result, badgeCounts]) => {
        if (cancelled) return;

        if (result !== null) {
          skillsCache.set(sKey, result);
          setActiveResult(result);
        }

        if (badgeCounts !== null) {
          badgesCache.set(bKey, badgeCounts);
          setActiveBadgeCounts(badgeCounts);
        }
      })
      .catch(() => {
        if (cancelled) return;
        addToast({
          color: 'negative',
          message: 'Failed to load skill path.'
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    sKey,
    bKey,
    classroomId,
    classrooms,
    filter,
    fetchToDoSkills,
    fetchAllSkills,
    fetchAdaptiveAchievement,
    fetchStudentDiagnostics,
    isAdaptiveDiagnosticsEnabled
  ]);

  const {subjectGroups, diagnostics} = activeResult ?? EMPTY_RESULT;
  return {
    skillSubjectGroups: subjectGroups,
    diagnostics,
    badgeCounts: activeBadgeCounts,
    isLoading: activeResult === null
  };
}
